import { Game } from "../engine/game";
import { generateProdLevel } from "../engine/levelgenerator/prodLevelGenerator";
import { Strings } from "../engine/strings";
import { AnimationLoader } from "./animationLoader";
import { DrawableManager } from "./drawableManager";
import { Camera } from "./camera";
import { SpriteRenderer } from "./spriteRenderer";
import { SoundManager } from "./sound/soundManager";
import { setUiSoundVolume } from "./sound/uiSoundPlayer";
import { MusicPlayer } from "./sound/musicPlayer";
import { UserSettings } from "./userSettings";
import * as TileImageLoader from "./tileImageLoader";
import { playerSpriteComposer } from "./playerSpriteComposer";
import { showSettingsScreen } from "./settingsScreen";
import { showCharacterSelect } from "./characterSelectScreen";
import { loadSeenHints, clearSeenHints } from "./hintStorage";
import { showConfirmDialog } from "./overlays";
import { createOverlay, createButton } from "./dom";
import {
	SAVE_KEY,
	createNewGame,
	createTutorialGame,
	createExperimentalGame,
} from "../gameFactory";
import { trackRunAbandoned, trackRunResume, trackRunStart } from "../telemetry/runPayloads";
import type { GameSession } from "../main";

export const TILESET_PATHS: Record<string, { assetsDir: string }> = {
	hd: { assetsDir: "/assets-hd/" },
};

const SHOW_EXPERIMENTAL_MODE = false;

export interface ShowMainMenuOptions {
	drawableManager: DrawableManager;
	camera: Camera;
	spriteRenderer: SpriteRenderer;
	animationLoader: AnimationLoader;
	audioContext: AudioContext;
	soundManager: SoundManager;
	musicPlayer: MusicPlayer;
	userSettings: UserSettings;
	startLevel: number;
	soundPreloadPromise: Promise<void>;
	getCurrentAssetsDir: () => string;
	setCurrentAssetsDir: (dir: string) => void;
	launchGame: (
		game: Game,
		drawableManager: DrawableManager,
		camera: Camera,
		spriteRenderer: SpriteRenderer,
		animationLoader: AnimationLoader,
		audioContext: AudioContext,
		soundManager: SoundManager,
		musicPlayer: MusicPlayer,
		userSettings: UserSettings,
		onQuitToMenu: (session: GameSession) => void,
	) => Promise<GameSession>;
}

export function showMainMenu(options: ShowMainMenuOptions): void {
	const {
		drawableManager,
		camera,
		spriteRenderer,
		animationLoader,
		audioContext,
		soundManager,
		musicPlayer,
		userSettings,
		startLevel,
		soundPreloadPromise,
		getCurrentAssetsDir,
		setCurrentAssetsDir,
		launchGame,
	} = options;

	// Reuse createOverlay for backdrop + lifecycle; discard the card panel (this isn't a modal).
	const overlay = createOverlay({ level: 1 });
	overlay.element.classList.add("opaque");
	overlay.panel.remove();

	const title = document.createElement("div");
	title.className = "main-menu-title";
	title.textContent = "Bullet Time";
	overlay.element.appendChild(title);

	const buttonContainer = document.createElement("div");
	buttonContainer.className = "main-menu-buttons";

	function quitToMenu(session: GameSession, clearSave: boolean = false): void {
		session.stop();
		if (clearSave) {
			localStorage.removeItem(SAVE_KEY);
		}
		showMainMenu(options);
	}

	let soundsReady = false;
	soundPreloadPromise.then(() => {
		soundsReady = true;
	});

	function startGameSession(game: Game): void {
		if (soundsReady) {
			overlay.close();
			launchGame(
				game,
				drawableManager,
				camera,
				spriteRenderer,
				animationLoader,
				audioContext,
				soundManager,
				musicPlayer,
				userSettings,
				(session) => quitToMenu(session),
			);
			return;
		}
		const loadingDiv = document.createElement("div");
		loadingDiv.className = "loading-screen";
		loadingDiv.setAttribute("data-loading-sounds", "");
		const text = document.createElement("div");
		text.className = "loading-screen-text";
		text.textContent = "Loading...";
		loadingDiv.appendChild(text);
		document.body.appendChild(loadingDiv);

		soundPreloadPromise.then(() => {
			document.body.removeChild(loadingDiv);
			overlay.close();
			launchGame(
				game,
				drawableManager,
				camera,
				spriteRenderer,
				animationLoader,
				audioContext,
				soundManager,
				musicPlayer,
				userSettings,
				(session) => quitToMenu(session),
			);
		});
	}

	function discardSaveAsAbandoned(): void {
		const existing = localStorage.getItem(SAVE_KEY);
		if (existing !== null) trackRunAbandoned(existing);
		localStorage.removeItem(SAVE_KEY);
	}

	function confirmIfSaveExists(action: () => void): void {
		const existing = localStorage.getItem(SAVE_KEY);
		if (!existing) {
			action();
			return;
		}
		// Tutorial saves aren't resumable, so no confirm needed.
		try {
			if (JSON.parse(existing).isTutorial === true) {
				action();
				return;
			}
		} catch {
			// Malformed save — fall through to confirm.
		}
		showConfirmDialog(
			Strings.get("ui_confirmLoseGame") ?? "Your current game will be lost. Are you sure?",
			action,
		);
	}

	// Tutorial saves are skipped — action closures don't survive serialization (bugs.txt #21).
	const savedData = localStorage.getItem(SAVE_KEY);
	let parsedSave = null;
	try {
		parsedSave = savedData ? JSON.parse(savedData) : null;
	} catch {
		parsedSave = null;
	}
	if (parsedSave && parsedSave.isTutorial !== true) {
		const continueBtn = createButton(
			Strings.get("ui_btnContinue") ?? "Continue",
			() => {
				const game = Game.deserialize(parsedSave);
				for (const id of loadSeenHints()) game.seenHints.add(id);
				game.levelGeneratorFn = (n, mapRng) => generateProdLevel(n, mapRng, game.seed);
				trackRunResume(game);
				startGameSession(game);
			},
			"primary",
		);
		buttonContainer.appendChild(continueBtn);
	}

	// Created here so closures capture the element; mounted later, under "Advanced".
	const seedInput = document.createElement("input");
	seedInput.type = "text";
	seedInput.className = "seed-input";
	seedInput.placeholder = Strings.get("ui_seedPlaceholder") ?? "Seed (optional)";

	const newGameBtn = createButton(
		Strings.get("ui_btnNewGame") ?? "New Game",
		() => {
			// Save discard happens on the final pick, so backing out of the character
			// select keeps an existing save intact.
			confirmIfSaveExists(() => {
				showCharacterSelect((characterId) => {
					discardSaveAsAbandoned();
					const customSeed = seedInput.value.trim() || undefined;
					const game = createNewGame(startLevel, customSeed, characterId);
					trackRunStart(game);
					startGameSession(game);
				});
			});
		},
		"secondary",
	);
	buttonContainer.appendChild(newGameBtn);

	if (SHOW_EXPERIMENTAL_MODE) {
		const experimentalBtn = createButton(
			Strings.get("ui_btnNewGameExperimental") ?? "New Game - Experimental",
			() => {
				confirmIfSaveExists(() => {
					discardSaveAsAbandoned();
					const customSeed = seedInput.value.trim() || undefined;
					const game = createExperimentalGame(startLevel, customSeed);
					trackRunStart(game);
					startGameSession(game);
				});
			},
			"secondary",
		);
		buttonContainer.appendChild(experimentalBtn);
	}

	const tutorialBtn = createButton(
		Strings.get("ui_btnPlayTutorial") ?? "Play Tutorial",
		() => {
			confirmIfSaveExists(() => {
				discardSaveAsAbandoned();
				const game = createTutorialGame();
				trackRunStart(game);
				startGameSession(game);
			});
		},
		"secondary",
	);
	buttonContainer.appendChild(tutorialBtn);

	const settingsBtn = createButton(
		Strings.get("ui_btnSettings") ?? "Settings",
		() => {
			showSettingsScreen(userSettings, async () => {
				const newPaths = TILESET_PATHS[userSettings.tileset] ?? TILESET_PATHS.hd;
				if (newPaths.assetsDir !== getCurrentAssetsDir()) {
					setCurrentAssetsDir(newPaths.assetsDir);
					TileImageLoader.setAssetsDir(newPaths.assetsDir);
					TileImageLoader.clearCache();
					playerSpriteComposer.clearCache();
					TileImageLoader.preloadAll();
				}
				soundManager.setVolumeLevel(userSettings.getEffectiveSoundVolume());
				setUiSoundVolume(userSettings.getEffectiveSoundVolume());
				musicPlayer.setVolume(userSettings.getEffectiveMusicVolume());
				// Rebuild so labels reflect new language.
				overlay.close();
				showMainMenu(options);
			}, () => {
				clearSeenHints();
			});
		},
		"secondary",
	);
	buttonContainer.appendChild(settingsBtn);

	const advancedSeparator = document.createElement("div");
	advancedSeparator.className = "menu-separator";
	advancedSeparator.textContent = Strings.get("ui_advancedLabel") ?? "Advanced";
	buttonContainer.appendChild(advancedSeparator);

	buttonContainer.appendChild(seedInput);

	overlay.element.appendChild(buttonContainer);
}
